import {
  Stack,
  Typography,
  Chip,
  Button,
  Box,
  CircularProgress,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton
} from '@mui/material';
import axios from 'axios';
import { Controller, useForm } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import { formatAppDate, formatAppDateTime, formatAppTime } from 'utils/dateFormat';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { InputSelect } from 'app/components/elements/InputSelect';
import { DatePicker } from 'app/components/elements/DatePicker';
import { useEffect, useMemo, useState } from 'react';
import { downloadFile } from 'utils/downloadFile';
import {
  getAdHocIssueDetail,
  updateAdHocIssue,
  updateAdHocSession,
  notifyAdHocIssue,
  AdHocIssue,
  PayloadUpdateAdHocIssue
} from 'services/report';
import { getIssuesConfig } from 'services/option';
import { DEFAULT_ISSUES_UI_CONFIG, IssuesUiConfig } from 'utils/issueDueDateColor';
import { DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';
import { IssueAssignmentFields, IssueAssignmentFormValues } from './IssueAssignmentFields';
import { IssueNotifyModal } from './IssueNotifyModal';
import { filterIssueNotificationUsers, resolveIssueClientId } from '../utils/issueNotifyUsers';
import { getLocations } from 'services/locations';
import { useRoomContext } from 'contexts/RoomContext';
import { PhotoManagement } from 'app/pages/ValidationsPage/components/elements/PhotoManagement';
import type { AdHocSession, AdHocIssueSessionTechnician } from 'types/report';
import { useFlash } from 'contexts/flash';

type Mode = 'create' | 'edit';

type AdHocIssueFormValues = IssueAssignmentFormValues & {
  clientId?: string;
  locationId?: string;
  name?: string;
  locationDescription?: string;
  description: string;
  resolvedAt?: Dayjs | null;
};

export const AdHocIssueDialog = ({
  mode,
  isOpen,
  issueId,
  canGoBack,
  onBack,
  onClose,
  onSaved,
  onOpenIssue
}: {
  mode: Mode;
  isOpen: boolean;
  issueId?: number;
  canGoBack?: boolean;
  onBack?: () => void;
  onClose: () => void;
  onSaved?: (issue: AdHocIssue) => void;
  /** When opening another issue (e.g. "View original" for duplicate), parent can set the issue to view */
  onOpenIssue?: (id: number) => void;
}) => {
  const { users } = useRoomContext();
  const { setFlash } = useFlash();

  const [loading, setLoading] = useState(false);
  const [issue, setIssue] = useState<AdHocIssue | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<'idle' | 'pending' | 'generated' | 'failed'>(
    'idle'
  );
  const [isExportTitleOpen, setIsExportTitleOpen] = useState(false);
  const [exportReportTitle, setExportReportTitle] = useState('');
  const [deletedLocationPhotoIds, setDeletedLocationPhotoIds] = useState<number[]>([]);
  const [deletedIssueBeforePhotoIds, setDeletedIssueBeforePhotoIds] = useState<number[]>([]);
  const [deletedIssueAfterPhotoIds, setDeletedIssueAfterPhotoIds] = useState<number[]>([]);
  const [roomOptions, setRoomOptions] = useState<{ label: string; value: string }[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const [newLocationPhotos, setNewLocationPhotos] = useState<File[]>([]);
  const [newIssueBeforePhotos, setNewIssueBeforePhotos] = useState<File[]>([]);
  const [newIssueAfterPhotos, setNewIssueAfterPhotos] = useState<File[]>([]);

  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [savingSessionId, setSavingSessionId] = useState<number | null>(null);
  const [editingSessionValues, setEditingSessionValues] = useState<{
    date: Dayjs | null;
    timeIn: Dayjs | null;
    timeOut: Dayjs | null;
    materialsUsed: string;
    equipmentUsed: string;
    technicianUserIds: number[];
  }>({
    date: null,
    timeIn: null,
    timeOut: null,
    materialsUsed: '',
    equipmentUsed: '',
    technicianUserIds: []
  });

  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);

  const { control, reset, handleSubmit, watch, setValue } = useForm<AdHocIssueFormValues>({
    defaultValues: {
      clientId: '',
      locationId: '',
      assignedToUserId: '',
      dueDate: null,
      status: 'ACTIVE',
      criticalityKey: '',
      name: '',
      locationDescription: '',
      description: '',
      resolvedAt: null
    }
  });
  const watchStatus = watch('status');

  useEffect(() => {
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG));
  }, []);

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const [notificationMessage, setNotificationMessage] = useState('');

  const [selectedNotificationUsers, setSelectedNotificationUsers] = useState<number[]>([]);
  const [selectedNotificationChannels, setSelectedNotificationChannels] = useState<string[]>([]);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

  // kept for compatibility with form interface; creation happens on mobile
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedClientId: string | undefined = undefined;

  useEffect(() => {
    const run = async () => {
      if (!isOpen) return;
      setIsEditing(false);

      setNewLocationPhotos([]);
      setNewIssueBeforePhotos([]);
      setNewIssueAfterPhotos([]);
      setDeletedLocationPhotoIds([]);
      setDeletedIssueBeforePhotoIds([]);
      setDeletedIssueAfterPhotoIds([]);

      if (!issueId) return;
      setLoading(true);
      try {
        const data = await getAdHocIssueDetail(issueId);
        setIssue(data.issue);
        reset({
          clientId: data.issue.client?.id ? String(data.issue.client.id) : '',
          locationId: data.issue.location?.id ? String(data.issue.location.id) : '',
          assignedToUserId: data.issue.assignedToUser?.id
            ? String(data.issue.assignedToUser.id)
            : '',
          dueDate: data.issue.dueDate ? dayjs(data.issue.dueDate) : null,
          status: data.issue.status || 'ACTIVE',
          criticalityKey: data.issue.criticalityKey || data.issue.criticality?.key || '',
          name: data.issue.name || `Adhoc Issue #${data.issue.id}`,
          locationDescription: data.issue.locationDescription || '',
          description: data.issue.description || '',
          resolvedAt: data.issue.resolvedAt ? dayjs(data.issue.resolvedAt) : null
        });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [isOpen, issueId, mode, reset]);

  // Load export status when opening a dialog.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isOpen || !issueId) return;
      setExportStatus('idle');
      try {
        const res = await axios.get(`/issues/ad-hoc/${issueId}/export/status`);
        if (cancelled) return;
        const next = res?.data?.status;
        if (next === 'pending' || next === 'generated' || next === 'failed' || next === 'idle') {
          setExportStatus(next);
        }
      } catch {
        if (!cancelled) setExportStatus('idle');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, issueId]);

  // Poll export status until generated.
  useEffect(() => {
    if (!isOpen || !issueId) return;
    if (exportStatus !== 'pending') return;

    let cancelled = false;
    const intervalId = setInterval(async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`/issues/ad-hoc/${issueId}/export/status`);
        const next = res?.data?.status;
        if (!cancelled && next && (next === 'generated' || next === 'failed' || next === 'idle')) {
          setExportStatus(next);
          if (next !== 'pending') {
            clearInterval(intervalId);
            if (next === 'generated') {
              setFlash({ type: 'success', message: 'PDF report generated successfully.' });
            } else if (next === 'failed') {
              setFlash({ type: 'error', message: 'PDF report generation failed.' });
            }
          }
        }
      } catch {
        // ignore polling errors for now
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [exportStatus, isOpen, issueId, setFlash]);

  // Load room options for "Reassign to room" when issue has a client
  useEffect(() => {
    const clientId = issue?.client?.id ?? issue?.clientId;
    if (!clientId) {
      setRoomOptions([]);
      return;
    }
    let cancelled = false;
    getLocations({
      clientId: String(clientId),
      page: 1,
      size: 500,
      keyword: '',
      type: '',
      orderBy: 'name',
      order: 'asc',
      parentId: ''
    })
      .then((data: { locations?: { id: number; name: string }[] }) => {
        if (cancelled) return;
        const list = data?.locations ?? [];
        setRoomOptions(
          list.map((loc: { id: number; name: string }) => ({
            label: loc.name ?? String(loc.id),
            value: String(loc.id)
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setRoomOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [issue?.client?.id, issue?.clientId]);

  const issueClientId = useMemo(() => resolveIssueClientId(issue), [issue]);

  const notificationUserOptions = useMemo(
    () => filterIssueNotificationUsers(users, issueClientId),
    [users, issueClientId]
  );

  useEffect(() => {
    const allowedIds = new Set(notificationUserOptions.map((u) => Number(u.value)));
    setSelectedNotificationUsers((prev) => prev.filter((id) => allowedIds.has(Number(id))));
  }, [notificationUserOptions]);

  const existingLocationPhotos = useMemo(() => {
    const items = (issue as AdHocIssue)?.locationPhotos;
    return Array.isArray(items) ? items : [];
  }, [issue]);

  const existingLocationPhotosForManagement = useMemo(() => {
    return existingLocationPhotos.map((p) => ({
      id: p.id ?? (p as { photoId?: number }).photoId ?? 0,
      url: p.photo?.url ?? '',
      storage: {
        fileName: p.photo?.fileName ?? '',
        type: p.photo?.type ?? ''
      }
    }));
  }, [existingLocationPhotos]);

  const existingBeforePhotos = useMemo(() => {
    const items = (issue as AdHocIssue)?.issuePhotosBefore;
    return Array.isArray(items) ? items : [];
  }, [issue]);

  const existingBeforePhotosForManagement = useMemo(() => {
    return existingBeforePhotos.map((p) => ({
      id: p.id ?? (p as { photoId?: number }).photoId ?? 0,
      url: p.photo?.url ?? '',
      storage: {
        fileName: p.photo?.fileName ?? '',
        type: p.photo?.type ?? ''
      }
    }));
  }, [existingBeforePhotos]);

  const existingAfterPhotos = useMemo(() => {
    const items = (issue as AdHocIssue)?.issuePhotosAfter;
    return Array.isArray(items) ? items : [];
  }, [issue]);

  const existingAfterPhotosForManagement = useMemo(() => {
    return existingAfterPhotos.map((p) => ({
      id: p.id ?? (p as { photoId?: number }).photoId ?? 0,
      url: p.photo?.url ?? '',
      storage: {
        fileName: p.photo?.fileName ?? '',
        type: p.photo?.type ?? ''
      }
    }));
  }, [existingAfterPhotos]);

  const totalMinutesSpent = useMemo(() => {
    const sessions = (issue as AdHocIssue)?.sessions as AdHocSession[];
    if (!Array.isArray(sessions)) return 0;
    return sessions.reduce((sum, s) => {
      if (s.timeIn && s.timeOut) {
        const diff = dayjs(s.timeOut).diff(dayjs(s.timeIn), 'minute');
        return sum + (diff > 0 ? diff : 0);
      }
      return sum;
    }, 0);
  }, [issue]);

  const formatTotalTimeString = (totalMinutes: number) => {
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const beginEditSession = (session: AdHocSession) => {
    const date = session.date ? dayjs(session.date) : dayjs();
    const timeIn = session.timeIn ? dayjs(session.timeIn) : null;
    const timeOut = session.timeOut ? dayjs(session.timeOut) : null;
    setEditingSessionId(session.id ?? null);
    setEditingSessionValues({
      date,
      timeIn,
      timeOut,
      materialsUsed: session.materialsUsed || '',
      equipmentUsed: session.equipmentUsed || '',
      technicianUserIds: Array.isArray(session.technicians)
        ? session.technicians
            .map((t: AdHocIssueSessionTechnician) => t?.id)
            .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
        : []
    });
  };

  const handleSessionFieldChange = (field: 'date' | 'timeIn' | 'timeOut', value: Dayjs | null) => {
    setEditingSessionValues((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSession = async (session: AdHocSession) => {
    if (!issueId || !session.id) return;
    const baseDate = editingSessionValues.date || (session.date ? dayjs(session.date) : null);
    const baseTimeIn =
      editingSessionValues.timeIn || (session.timeIn ? dayjs(session.timeIn) : null);
    const baseTimeOut =
      editingSessionValues.timeOut || (session.timeOut ? dayjs(session.timeOut) : null);

    if (!baseDate || !baseTimeIn) {
      setFlash({ type: 'error', message: 'Date and Time In are required.' });
      return;
    }
    const sessionHasEnded = !!session.timeOut;
    if (sessionHasEnded && !baseTimeOut) {
      setFlash({ type: 'error', message: 'Time Out is required for ended sessions.' });
      return;
    }

    const payload: {
      date?: string;
      timeIn?: string;
      timeOut?: string | null;
      materialsUsed?: string;
      equipmentUsed?: string;
      technicianUserIds?: number[];
    } = {};

    if (baseDate) {
      payload.date = baseDate.toISOString();
    }
    if (baseTimeIn) {
      payload.timeIn = baseTimeIn.toISOString();
    }
    if (baseTimeOut) {
      payload.timeOut = baseTimeOut.toISOString();
    } else if (session.timeOut === null) {
      payload.timeOut = null;
    }

    payload.materialsUsed =
      editingSessionValues.materialsUsed != null ? editingSessionValues.materialsUsed : undefined;
    payload.equipmentUsed =
      editingSessionValues.equipmentUsed != null ? editingSessionValues.equipmentUsed : undefined;
    payload.technicianUserIds = Array.isArray(editingSessionValues.technicianUserIds)
      ? editingSessionValues.technicianUserIds
      : [];

    setSavingSessionId(session.id);
    try {
      const res = await updateAdHocSession(issueId, session.id, payload);
      const updatedSession = res.session as AdHocSession;
      if (issue && Array.isArray((issue as AdHocIssue).sessions)) {
        const nextSessions = ((issue as AdHocIssue).sessions as AdHocSession[]).map((s) =>
          s.id === updatedSession.id ? updatedSession : s
        );
        setIssue({
          ...(issue as AdHocIssue),
          sessions: nextSessions as unknown[]
        });
      }
      setFlash({
        type: 'success',
        message: 'Session updated successfully.'
      });
      setExportStatus('idle');
      setEditingSessionId(null);
    } finally {
      setSavingSessionId(null);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!issueId) return;
    if (!isEditing) {
      setIsEditing(true);
      return;
    }
    const payload: PayloadUpdateAdHocIssue = {
      assignedToUserId: values.assignedToUserId ? Number(values.assignedToUserId) : undefined,
      dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
      criticalityKey: values.criticalityKey || null,
      status: values.status || undefined,
      resolvedAt:
        values.status === 'RESOLVED'
          ? values.resolvedAt
            ? values.resolvedAt.toISOString()
            : dayjs().toISOString()
          : undefined,
      name: values.name?.trim() || undefined,
      locationDescription: values.locationDescription || undefined,
      description: values.description || undefined,
      locationPhotos: newLocationPhotos.length ? newLocationPhotos : undefined,
      issuePhotosBefore: newIssueBeforePhotos.length ? newIssueBeforePhotos : undefined,
      issuePhotosAfter: newIssueAfterPhotos.length ? newIssueAfterPhotos : undefined,
      deletedLocationPhotoIds: deletedLocationPhotoIds.length ? deletedLocationPhotoIds : undefined,
      deletedIssueBeforePhotoIds: deletedIssueBeforePhotoIds.length
        ? deletedIssueBeforePhotoIds
        : undefined,
      deletedIssueAfterPhotoIds: deletedIssueAfterPhotoIds.length
        ? deletedIssueAfterPhotoIds
        : undefined
    };
    if (values.locationId !== undefined && values.locationId !== '') {
      payload.locationId = values.locationId ? Number(values.locationId) : null;
    }
    const res = await updateAdHocIssue(issueId, payload);
    onSaved?.(res.issue);
    setIssue(res.issue);
    reset({
      clientId: res.issue.client?.id ? String(res.issue.client.id) : '',
      locationId: res.issue.location?.id ? String(res.issue.location.id) : '',
      assignedToUserId: res.issue.assignedToUser?.id ? String(res.issue.assignedToUser.id) : '',
      dueDate: res.issue.dueDate ? dayjs(res.issue.dueDate) : null,
      status: res.issue.status || 'ACTIVE',
      criticalityKey: res.issue.criticalityKey || res.issue.criticality?.key || '',
      name: res.issue.name || `Adhoc Issue #${res.issue.id}`,
      locationDescription: res.issue.locationDescription || '',
      description: res.issue.description || '',
      resolvedAt: res.issue.resolvedAt ? dayjs(res.issue.resolvedAt) : null
    });
    setNewLocationPhotos([]);
    setNewIssueBeforePhotos([]);
    setNewIssueAfterPhotos([]);
    setDeletedLocationPhotoIds([]);
    setDeletedIssueBeforePhotoIds([]);
    setDeletedIssueAfterPhotoIds([]);
    setFlash({
      type: 'success',
      message: 'Issue updated successfully.'
    });
    setExportStatus('idle');
    setIsEditing(false);
  });

  const handleMarkAsDuplicate = async () => {
    if (!issueId) return;
    setActionLoading('archive');
    try {
      const res = await updateAdHocIssue(issueId, { status: 'ARCHIVED' });
      onSaved?.(res.issue);
      setExportStatus('idle');
      setFlash({
        type: 'success',
        message: 'Issue marked as duplicate and archived.'
      });
      onClose();
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmUnique = async () => {
    if (!issueId) return;
    setActionLoading('unique');
    try {
      const res = await updateAdHocIssue(issueId, { possibleDuplicateOfId: null });
      onSaved?.(res.issue);
      setIssue(res.issue);
      setExportStatus('idle');
      setFlash({
        type: 'success',
        message: 'Issue confirmed as unique.'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportSessionsPdf = async () => {
    if (!issueId) return;
    const hasSessions =
      Array.isArray((issue as AdHocIssue)?.sessions) &&
      ((issue as AdHocIssue)?.sessions?.length ?? 0) > 0;
    if (!hasSessions) return;

    const defaultTitle =
      (issue as AdHocIssue)?.name ||
      `Adhoc Issue #${issueId} sessions ${dayjs().format('YYYY-MM-DD')}`;
    setExportReportTitle(defaultTitle);
    setIsExportTitleOpen(true);
  };

  const handleConfirmExportSessionsPdf = async () => {
    if (!issueId) return;
    const title = exportReportTitle.trim();
    if (!title) {
      setFlash({ type: 'error', message: 'Report name is required.' });
      return;
    }

    try {
      setIsExportTitleOpen(false);
      setExportStatus('pending');
      await axios.get(`/issues/ad-hoc/${issueId}/export`, { params: { title } });
      setFlash({
        type: 'success',
        message: 'PDF report generation started. It will also appear under Reports.'
      });
    } catch (err) {
      setExportStatus('failed');
      setFlash({ type: 'error', message: 'Failed to export sessions PDF.' });
    }
  };

  const handleDownloadExportSessionsPdf = async () => {
    if (!issueId) return;
    try {
      await downloadFile(`/issues/ad-hoc/${issueId}/export/download`, {});
    } catch (err) {
      setFlash({ type: 'error', message: 'Failed to download exported sessions PDF.' });
    }
  };

  const handleSendManualNotification = async () => {
    if (!issueId) return;
    if (selectedNotificationUsers.length === 0) {
      setFlash({ type: 'error', message: 'Please select at least one user.' });
      return;
    }
    if (selectedNotificationChannels.length === 0) {
      setFlash({ type: 'error', message: 'Please select at least one channel.' });
      return;
    }

    setIsSendingNotification(true);
    try {
      const res = await notifyAdHocIssue(issueId, {
        userIds: selectedNotificationUsers,
        channels: selectedNotificationChannels,
        message: notificationMessage
      });
      setFlash({
        type: 'success',
        message: res?.message || 'Notifications sent successfully.'
      });
      setNotificationMessage('');
      setSelectedNotificationUsers([]);
      setSelectedNotificationChannels([]);
      setIsNotifyModalOpen(false);
    } catch (err) {
      setFlash({ type: 'error', message: 'Failed to send notifications.' });
    } finally {
      setIsSendingNotification(false);
    }
  };

  const formatLocationWithIds = (duplicate: {
    locationPathString?: string | null;
    locationLevelIds?: {
      l1Id?: number;
      l2Id?: number;
      l3Id?: number;
      l4Id?: number;
      l5Id?: number;
    };
  }) => {
    const path = duplicate.locationPathString || '-';
    const ids = [
      duplicate.locationLevelIds?.l1Id,
      duplicate.locationLevelIds?.l2Id,
      duplicate.locationLevelIds?.l3Id,
      duplicate.locationLevelIds?.l4Id,
      duplicate.locationLevelIds?.l5Id
    ].filter((id): id is number => typeof id === 'number');
    return ids.length ? `${path} (${ids.join(' > ')})` : path;
  };

  return (
    <>
      <DialogConfirm
        isOpen={isOpen}
        onClose={onClose}
        textTitle="Ad Hoc Issue"
        textButton={isEditing ? 'Save' : 'Edit'}
        textCancel="Close"
        onSubmit={onSubmit}
        isIconClose
        maxWidth={1250}
        headerActions={
          <IconButton
            onClick={() => setIsNotifyModalOpen(true)}
            sx={{ color: '#2B3492', p: 0.5 }}
            title="Manual Notification">
            <NotificationsActiveIcon />
          </IconButton>
        }>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack width="100%" gap={2}>
            {canGoBack && onBack && (
              <Stack direction="row" justifyContent="flex-start">
                <Button size="small" variant="text" onClick={onBack}>
                  Back
                </Button>
              </Stack>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
              {/* Left column: assignment + issue details */}
              <Stack flex={2} gap={2}>
                <Stack
                  gap={1}
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid #E0E0E0',
                    p: 2,
                    bgcolor: '#ffffff'
                  }}>
                  <Typography fontWeight={700} color="#226AAA">
                    Assignment Information
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Stack flex={1} gap={0.5}>
                      <Typography fontWeight={700}>Reported By</Typography>
                      <Typography>{issue?.reportedByUser?.name || '-'}</Typography>
                    </Stack>
                    <Stack flex={1} gap={0.5}>
                      <Typography fontWeight={700}>Reported Date</Typography>
                      <Typography>
                        {issue?.createdAt ? formatAppDateTime(issue.createdAt) : '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Stack flex={1} gap={0.5}>
                      <Typography fontWeight={700}>Client</Typography>
                      <Typography>{issue?.client?.clientName || '-'}</Typography>
                    </Stack>
                    <Stack flex={1} gap={0.5}>
                      <Typography fontWeight={700}>Location</Typography>
                      <Typography>
                        {issue?.locationPathString || issue?.location?.name || '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-end">
                    <Stack flex={1} gap={0.5}>
                      <Typography fontWeight={700}>Assigned By</Typography>
                      <Typography>{issue?.assignedByUser?.name || '-'}</Typography>
                    </Stack>
                    <Stack flex={1} gap={0.5}>
                      <Typography fontWeight={700}>Date Resolved</Typography>
                      <Typography>
                        {issue?.resolvedAt ? formatAppDateTime(issue.resolvedAt) : '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontWeight={700}>Latitude</Typography>
                    <Typography>{issue?.lat ?? '-'}</Typography>
                    <Typography fontWeight={700} ml={3}>
                      Longitude
                    </Typography>
                    <Typography>{issue?.long ?? '-'}</Typography>
                    <Typography fontWeight={700} ml={3}>
                      Altitude
                    </Typography>
                    <Typography>{issue?.altitude != null ? `${issue.altitude} m` : '-'}</Typography>
                  </Stack>
                </Stack>

                {(issue?.possibleDuplicateOfId || issue?.isPotentialDuplicate) &&
                  issue?.possibleDuplicateOf && (
                    <Stack
                      gap={1.5}
                      sx={{
                        borderRadius: 1.5,
                        border: '1px solid #FF9800',
                        p: 2,
                        bgcolor: '#FFF8E1'
                      }}>
                      <Typography fontWeight={700} color="#E65100">
                        Potential duplicate
                      </Typography>
                      <Typography variant="body2">
                        This issue was flagged as a possible duplicate of an existing one (same and
                        and within ~10m). Review and choose an action below.
                      </Typography>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2">
                          Original issue #{issue.possibleDuplicateOf.id}:{' '}
                          {issue.possibleDuplicateOf.locationPathString ??
                            issue.possibleDuplicateOf.description ??
                            '-'}
                        </Typography>
                        {onOpenIssue && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onOpenIssue(issue.possibleDuplicateOf!.id)}>
                            View original
                          </Button>
                        )}
                      </Stack>
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="contained"
                          color="inherit"
                          disabled={!!actionLoading}
                          onClick={handleMarkAsDuplicate}>
                          {actionLoading === 'archive' ? '...' : 'Mark as duplicate (archive)'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          disabled={!!actionLoading}
                          onClick={handleConfirmUnique}>
                          {actionLoading === 'unique' ? '...' : 'Confirm as unique but nearby'}
                        </Button>
                      </Stack>
                      {roomOptions.length > 0 && (
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={600}>
                            Reassign to room:
                          </Typography>
                          <Controller
                            control={control}
                            name="locationId"
                            render={({ field: { onChange, value } }) => (
                              <InputSelect
                                value={value || ''}
                                placeholder="Select room"
                                onChange={(e) => onChange(String(e.target.value))}
                                sx={{ minWidth: 200, minHeight: 36 }}
                                options={[{ label: '— Keep current —', value: '' }, ...roomOptions]}
                              />
                            )}
                          />
                        </Stack>
                      )}
                    </Stack>
                  )}

                <Stack
                  gap={2}
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid #E0E0E0',
                    p: 2,
                    bgcolor: '#ffffff'
                  }}>
                  <Typography fontWeight={700} color="#226AAA">
                    Issue Details
                  </Typography>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <TextField
                        label="Issue Name"
                        placeholder="Issue Name"
                        {...field}
                        disabled={!isEditing}
                        sx={{ width: '100%' }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="locationDescription"
                    render={({ field }) => (
                      <TextField
                        label="Location Description"
                        placeholder="Location Description"
                        {...field}
                        disabled={!isEditing}
                        multiline
                        minRows={2}
                        sx={{ width: '100%', '& textarea': { resize: 'vertical' } }}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <TextField
                        label="Issue Description"
                        placeholder="Issue Description"
                        {...field}
                        disabled={!isEditing}
                        multiline
                        minRows={2}
                        sx={{ width: '100%', '& textarea': { resize: 'vertical' } }}
                      />
                    )}
                  />

                  <IssueAssignmentFields
                    control={control}
                    isEditing={isEditing}
                    users={users || []}
                    criticalityLevels={criticalityLevels}
                    onStatusChange={(next) => {
                      if (next === 'RESOLVED' && !watch('resolvedAt')) {
                        setValue('resolvedAt', dayjs());
                      }
                    }}
                  />
                  {watchStatus === 'RESOLVED' && (
                    <Controller
                      control={control}
                      name="resolvedAt"
                      render={({ field: { value, onChange } }) => (
                        <DatePicker
                          includeTime
                          title="Date Resolved"
                          value={value || null}
                          onChange={(next) => onChange(next)}
                          dateStyle={{
                            pointerEvents: isEditing ? 'auto' : 'none',
                            opacity: isEditing ? 1 : 0.6
                          }}
                        />
                      )}
                    />
                  )}
                </Stack>

                <Stack
                  gap={2}
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid #E0E0E0',
                    p: 2,
                    bgcolor: '#ffffff'
                  }}>
                  <Typography fontWeight={700} color="#226AAA">
                    Photo Documentation
                  </Typography>

                  <Stack gap={1.5}>
                    <PhotoManagement
                      sectionLabel="Location Photos"
                      existingPhotos={existingLocationPhotosForManagement}
                      newPhotos={newLocationPhotos}
                      deletedPhotoIds={deletedLocationPhotoIds}
                      disabled={!isEditing}
                      onAddPhotos={(files) => setNewLocationPhotos((prev) => [...prev, ...files])}
                      onDeleteExistingPhoto={(photoId) =>
                        photoId > 0 &&
                        setDeletedLocationPhotoIds((prev) =>
                          prev.includes(photoId) ? prev : [...prev, photoId]
                        )
                      }
                      onRestorePhoto={(photoId) =>
                        setDeletedLocationPhotoIds((prev) => prev.filter((id) => id !== photoId))
                      }
                      onRemoveNewPhoto={(index) =>
                        setNewLocationPhotos((prev) => prev.filter((_, i) => i !== index))
                      }
                      compactUpload
                    />
                  </Stack>

                  <Stack gap={1.5}>
                    <PhotoManagement
                      sectionLabel="Issue Photos Before"
                      existingPhotos={existingBeforePhotosForManagement}
                      newPhotos={newIssueBeforePhotos}
                      deletedPhotoIds={deletedIssueBeforePhotoIds}
                      disabled={!isEditing}
                      onAddPhotos={(files) =>
                        setNewIssueBeforePhotos((prev) => [...prev, ...files])
                      }
                      onDeleteExistingPhoto={(photoId) =>
                        photoId > 0 &&
                        setDeletedIssueBeforePhotoIds((prev) =>
                          prev.includes(photoId) ? prev : [...prev, photoId]
                        )
                      }
                      onRestorePhoto={(photoId) =>
                        setDeletedIssueBeforePhotoIds((prev) => prev.filter((id) => id !== photoId))
                      }
                      onRemoveNewPhoto={(index) =>
                        setNewIssueBeforePhotos((prev) => prev.filter((_, i) => i !== index))
                      }
                      compactUpload
                    />
                  </Stack>

                  {mode === 'edit' && (
                    <Stack gap={1.5}>
                      <PhotoManagement
                        sectionLabel="Issue Photos After"
                        existingPhotos={existingAfterPhotosForManagement}
                        newPhotos={newIssueAfterPhotos}
                        deletedPhotoIds={deletedIssueAfterPhotoIds}
                        disabled={!isEditing}
                        onAddPhotos={(files) =>
                          setNewIssueAfterPhotos((prev) => [...prev, ...files])
                        }
                        onDeleteExistingPhoto={(photoId) =>
                          photoId > 0 &&
                          setDeletedIssueAfterPhotoIds((prev) =>
                            prev.includes(photoId) ? prev : [...prev, photoId]
                          )
                        }
                        onRestorePhoto={(photoId) =>
                          setDeletedIssueAfterPhotoIds((prev) =>
                            prev.filter((id) => id !== photoId)
                          )
                        }
                        onRemoveNewPhoto={(index) =>
                          setNewIssueAfterPhotos((prev) => prev.filter((_, i) => i !== index))
                        }
                        compactUpload
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>

              {/* Right column: sessions overview */}
              <Stack
                flex={1.1}
                gap={2}
                sx={{
                  borderRadius: 1.5,
                  border: '1px solid #E0E0E0',
                  p: 2,
                  bgcolor: '#ffffff',
                  minWidth: 260
                }}>
                {Array.isArray((issue as AdHocIssue)?.duplicateIssues) &&
                  ((issue as AdHocIssue)?.duplicateIssues?.length ?? 0) > 0 && (
                    <Stack gap={1.5}>
                      <Typography fontWeight={700} color="#226AAA">
                        Duplicate issues
                      </Typography>
                      <Stack direction="column" gap={1.5}>
                        {(issue as AdHocIssue)?.duplicateIssues?.map((d) => (
                          <Stack
                            key={String(d.id)}
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            gap={1.5}
                            sx={{
                              borderRadius: 1.5,
                              border: '1px solid #FF9800',
                              p: 2,
                              bgcolor: '#FFF8E1'
                            }}>
                            <Stack flex={1} gap={1}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={1}>
                                <Typography fontWeight={700} color="#E65100">
                                  Duplicate issue #{String(d.id)}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => onOpenIssue?.(Number(d.id))}
                                  disabled={!onOpenIssue}>
                                  View
                                </Button>
                              </Stack>

                              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                <Stack flex={1} gap={0.5}>
                                  <Typography fontWeight={700}>Reported By</Typography>
                                  <Typography>{d.reportedByUser?.name || '-'}</Typography>
                                </Stack>
                                <Stack flex={1} gap={0.5}>
                                  <Typography fontWeight={700}>Reported Date</Typography>
                                  <Typography>
                                    {d.createdAt ? formatAppDateTime(d.createdAt) : '-'}
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Stack gap={0.5}>
                                <Typography fontWeight={700}>Issue Description</Typography>
                                <Typography whiteSpace="pre-line">
                                  {d.description || '-'}
                                </Typography>
                              </Stack>

                              <Stack gap={0.5}>
                                <Typography fontWeight={700}>Location</Typography>
                                <Typography>{formatLocationWithIds(d)}</Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontWeight={700} color="#226AAA">
                    Sessions
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={`${(issue as AdHocIssue)?.sessions?.length ?? 0} logs found`}
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: '#FFF3E0',
                        color: '#FB8C00'
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={
                        exportStatus === 'pending' ||
                        !issueId ||
                        !Array.isArray((issue as AdHocIssue)?.sessions) ||
                        (issue as AdHocIssue)?.sessions?.length === 0
                      }
                      onClick={handleExportSessionsPdf}>
                      {exportStatus === 'pending' ? 'Generating...' : 'Generate PDF'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={
                        exportStatus !== 'generated' ||
                        !issueId ||
                        !Array.isArray((issue as AdHocIssue)?.sessions) ||
                        (issue as AdHocIssue)?.sessions?.length === 0
                      }
                      onClick={handleDownloadExportSessionsPdf}>
                      Download
                    </Button>
                  </Stack>
                </Stack>
                {((issue as AdHocIssue)?.sessions?.length ?? 0) > 0 && totalMinutesSpent >= 0 && (
                  <Typography
                    variant="caption"
                    color="#226AAA"
                    fontWeight={700}
                    sx={{ mt: -0.5, mb: 1, textAlign: 'right', display: 'block' }}>
                    Total hours spent: {formatTotalTimeString(totalMinutesSpent)} (Completed
                    sessions)
                  </Typography>
                )}
                {Array.isArray((issue as AdHocIssue)?.sessions) &&
                ((issue as AdHocIssue)?.sessions?.length ?? 0) > 0 ? (
                  ((issue as AdHocIssue)?.sessions as AdHocSession[])?.map(
                    (session: AdHocSession, index: number) => {
                      const isEditing = editingSessionId === session.id;
                      return (
                        <Stack
                          key={session.id ?? index}
                          gap={1}
                          sx={{
                            borderRadius: 1.5,
                            border: '1px solid #E5E7EB',
                            p: 1.5,
                            bgcolor: '#F9FAFB'
                          }}>
                          <Stack mb={0.5}>
                            {!isEditing ? (
                              // VIEW MODE (unchanged layout)
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                gap={0.25}
                                alignItems="flex-end">
                                <Stack gap={0.25} flex={1}>
                                  <Typography variant="caption" color="text.secondary">
                                    DATE
                                  </Typography>
                                  <Typography variant="subtitle2">
                                    {session.date ? formatAppDate(session.date) : '-'}
                                  </Typography>
                                </Stack>

                                <Stack gap={0.25} flex={1}>
                                  <Typography variant="caption" color="text.secondary">
                                    TIME IN
                                  </Typography>
                                  <Typography variant="subtitle2">
                                    {session.timeIn ? formatAppTime(session.timeIn) : '-'}
                                  </Typography>
                                </Stack>

                                <Stack gap={0.25} flex={1}>
                                  <Typography variant="caption" color="text.secondary">
                                    TIME OUT
                                  </Typography>
                                  <Typography variant="subtitle2">
                                    {session.timeOut
                                      ? formatAppTime(session.timeOut)
                                      : 'In progress'}
                                  </Typography>
                                </Stack>
                              </Stack>
                            ) : (
                              // EDIT MODE (new layout)
                              <Stack gap={1}>
                                {/* DATE FULL WIDTH */}
                                <Stack gap={0.25}>
                                  <Typography variant="caption" color="text.secondary">
                                    DATE
                                  </Typography>

                                  <DatePicker
                                    title=""
                                    value={editingSessionValues.date}
                                    onChange={(next) => handleSessionFieldChange('date', next)}
                                    dateStyle={{
                                      '& .MuiInputBase-root': {
                                        minHeight: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: '6px 10px'
                                      }
                                    }}
                                  />
                                </Stack>
                                {/* TIME FIELDS */}
                                <Stack direction="row" gap={1}>
                                  <Stack gap={0.25} flex={1}>
                                    <Typography variant="caption" color="text.secondary">
                                      TIME IN
                                    </Typography>
                                    <TextField
                                      type="time"
                                      size="small"
                                      sx={{
                                        color: '#000',
                                        backgroundColor: '#fff'
                                      }}
                                      fullWidth
                                      value={
                                        editingSessionValues.timeIn
                                          ? editingSessionValues.timeIn.format('HH:mm')
                                          : session.timeIn
                                            ? formatAppTime(session.timeIn)
                                            : ''
                                      }
                                      onChange={(e) =>
                                        handleSessionFieldChange(
                                          'timeIn',
                                          e.target.value
                                            ? dayjs(
                                                (
                                                  editingSessionValues.date ||
                                                  (session.date ? dayjs(session.date) : dayjs())
                                                ).format('YYYY-MM-DD') + `T${e.target.value}`
                                              )
                                            : null
                                        )
                                      }
                                      inputProps={{ step: 300 }}
                                    />
                                  </Stack>

                                  <Stack gap={0.25} flex={1}>
                                    <Typography variant="caption" color="text.secondary">
                                      TIME OUT
                                    </Typography>
                                    <TextField
                                      type="time"
                                      size="small"
                                      fullWidth
                                      sx={{
                                        color: '#000',
                                        backgroundColor: '#fff'
                                      }}
                                      value={
                                        editingSessionValues.timeOut
                                          ? editingSessionValues.timeOut.format('HH:mm')
                                          : session.timeOut
                                            ? formatAppTime(session.timeOut)
                                            : ''
                                      }
                                      onChange={(e) =>
                                        handleSessionFieldChange(
                                          'timeOut',
                                          e.target.value
                                            ? dayjs(
                                                (
                                                  editingSessionValues.date ||
                                                  (session.date ? dayjs(session.date) : dayjs())
                                                ).format('YYYY-MM-DD') + `T${e.target.value}`
                                              )
                                            : null
                                        )
                                      }
                                      inputProps={{ step: 300 }}
                                    />
                                  </Stack>
                                </Stack>

                                <Stack gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    MATERIALS USED
                                  </Typography>
                                  <TextField
                                    size="small"
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    value={editingSessionValues.materialsUsed}
                                    onChange={(e) =>
                                      setEditingSessionValues((prev) => ({
                                        ...prev,
                                        materialsUsed: e.target.value
                                      }))
                                    }
                                    sx={{ '& textarea': { resize: 'vertical' } }}
                                  />
                                </Stack>

                                <Stack gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    EQUIPMENT USED
                                  </Typography>
                                  <TextField
                                    size="small"
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    value={editingSessionValues.equipmentUsed}
                                    onChange={(e) =>
                                      setEditingSessionValues((prev) => ({
                                        ...prev,
                                        equipmentUsed: e.target.value
                                      }))
                                    }
                                    sx={{ '& textarea': { resize: 'vertical' } }}
                                  />
                                </Stack>

                                <Stack gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    OTHER TECHNICIANS PRESENT
                                  </Typography>
                                  <TextField
                                    select
                                    SelectProps={{
                                      multiple: true,
                                      renderValue: (selected) => {
                                        const ids = Array.isArray(selected)
                                          ? (selected as number[])
                                          : [];
                                        const names = ids
                                          .map(
                                            (id) =>
                                              users?.find((u) => String(u.value) === String(id))
                                                ?.label
                                          )
                                          .filter(Boolean);
                                        return names.length ? names.join(', ') : '—';
                                      }
                                    }}
                                    size="small"
                                    value={editingSessionValues.technicianUserIds}
                                    onChange={(e) => {
                                      const v = e.target.value as unknown;
                                      setEditingSessionValues((prev) => ({
                                        ...prev,
                                        technicianUserIds: Array.isArray(v) ? (v as number[]) : []
                                      }));
                                    }}>
                                    {(users || []).map((u) => {
                                      const id = Number(u.value);
                                      const checked =
                                        editingSessionValues.technicianUserIds.includes(id);
                                      return (
                                        <MenuItem key={String(u.value)} value={id}>
                                          <Checkbox checked={checked} />
                                          <ListItemText primary={u.label} />
                                        </MenuItem>
                                      );
                                    })}
                                  </TextField>
                                </Stack>
                              </Stack>
                            )}
                          </Stack>

                          {!isEditing && (
                            <>
                              <Stack gap={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  MATERIALS USED
                                </Typography>
                                <Typography variant="body2">
                                  {session.materialsUsed && session.materialsUsed.trim().length
                                    ? session.materialsUsed
                                    : 'None'}
                                </Typography>
                              </Stack>

                              <Stack gap={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  EQUIPMENT USED
                                </Typography>
                                <Typography variant="body2">
                                  {session.equipmentUsed && session.equipmentUsed.trim().length
                                    ? session.equipmentUsed
                                    : 'None'}
                                </Typography>
                              </Stack>

                              <Stack gap={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  OTHER TECHNICIANS PRESENT
                                </Typography>
                                {Array.isArray(session.technicians) &&
                                session.technicians.length > 0 ? (
                                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {session.technicians
                                      ?.map((t: AdHocIssueSessionTechnician) => t?.name)
                                      .filter((name): name is string => !!name)
                                      .map((name) => (
                                        <Chip
                                          key={name}
                                          size="small"
                                          label={name}
                                          sx={{
                                            bgcolor: '#E3F2FD',
                                            color: '#1976D2',
                                            fontSize: 10,
                                            fontWeight: 700
                                          }}
                                        />
                                      ))}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2">—</Typography>
                                )}
                              </Stack>
                            </>
                          )}

                          <Stack direction="row" justifyContent="flex-end" gap={1} mt={1}>
                            {isEditing ? (
                              <>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setEditingSessionId(null)}
                                  disabled={savingSessionId === session.id}>
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSaveSession(session)}
                                  disabled={savingSessionId === session.id}>
                                  {savingSessionId === session.id ? 'Saving...' : 'Save'}
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => beginEditSession(session)}>
                                Edit
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      );
                    }
                  )
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No sessions recorded yet.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
      </DialogConfirm>

      <IssueNotifyModal
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        onSubmit={handleSendManualNotification}
        disabled={isSendingNotification}
        issuesConfig={issuesConfig}
        userOptions={notificationUserOptions}
        selectedUserIds={selectedNotificationUsers}
        onSelectedUserIdsChange={setSelectedNotificationUsers}
        selectedChannels={selectedNotificationChannels}
        onSelectedChannelsChange={setSelectedNotificationChannels}
        message={notificationMessage}
        onMessageChange={setNotificationMessage}
      />

      <DialogConfirm
        isOpen={isExportTitleOpen}
        onClose={() => setIsExportTitleOpen(false)}
        onSubmit={handleConfirmExportSessionsPdf}
        textTitle="Report name"
        textContent="Enter a name for this session report. It will appear under Reports."
        textButton="Generate PDF"
        textCancel="Cancel"
        maxWidth={480}
        disabled={!exportReportTitle.trim()}>
        <TextField
          fullWidth
          size="small"
          label="Report name"
          required
          value={exportReportTitle}
          onChange={(e) => setExportReportTitle(e.target.value)}
          inputProps={{ maxLength: 255 }}
        />
      </DialogConfirm>
    </>
  );
};
